import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Star, 
  Quote, 
  Heart, 
  Shield, 
  PawPrint, 
  Sparkles,
  BadgeCheck,
  ThumbsUp,
  ChevronRight,
  ChevronLeft,
  MessageCircle,
  Users,
  Clock,
  CheckCircle2,
  Calendar,
  Zap
} from 'lucide-react'
import { Button } from "@/components/ui/button"

const TestimonialsSection = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  
  // Autoplay functionality
  useEffect(() => {
    if (!autoplay) return;
    
    const interval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % testimonials.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [autoplay]);

  const statistics = [
    {
      value: "98%",
      label: "Satisfacción",
      icon: Heart,
      description: "de dueños satisfechos"
    },
    {
      value: "+10,000",
      label: "Mascotas felices",
      icon: PawPrint,
      description: "analizadas"
    },
    {
      value: "24/7",
      label: "Soporte disponible",
      icon: Shield,
      description: "atención continua"
    },
    {
      value: "15min",
      label: "Tiempo promedio",
      icon: Clock,
      description: "de respuesta"
    }
  ];

  const testimonials = [
    {
      name: "María García",
      role: "Dueña de Luna",
      breed: "Labrador Retriever",
      image: "/people2.jpeg",
      rating: 5,
      date: "Hace 1 semana",
      content: "Gracias a DogScan detectamos temprano un problema nutricional en Luna. La precisión del análisis y las recomendaciones personalizadas nos ayudaron a tomar medidas preventivas. El servicio de consulta veterinaria 24/7 nos da mucha tranquilidad.",
      verifiedBadge: true,
      location: "Ciudad de México",
      petImage: "/perrito14.jpeg",
      benefits: ["Detección temprana", "Consultas ilimitadas", "Seguimiento personalizado"],
      followUp: "Después de seguir las recomendaciones, Luna ha mejorado notablemente su salud y energía."
    },
    {
      name: "Carlos Ruiz",
      role: "Dueño de Max",
      breed: "Golden Retriever",
      image: "/people1.jpeg",
      rating: 5,
      date: "Hace 1 semana",
      content: "Las recomendaciones personalizadas han mejorado significativamente la salud de Max. Es increíble cómo la IA puede detectar patrones que no son evidentes a simple vista. El seguimiento continuo y los reportes detallados son muy útiles.",
      verifiedBadge: true,
      location: "Guadalajara",
      petImage: "/perrito12.jpeg",
      benefits: ["Análisis preventivo", "Reportes detallados", "Consultas con especialistas"],
      followUp: "Max ahora tiene un plan nutricional personalizado que ha mejorado su pelaje y nivel de energía."
    },
    {
      name: "Rodrigo Quintanar",
      role: "Dueño de Rocky",
      breed: "Bulldog Francés",
      image: "/profile3.png",
      rating: 5,
      date: "Hace 1 semana",
      content: "La facilidad de uso y la precisión del análisis son impresionantes. Los veterinarios en línea son muy profesionales y las consultas son muy convenientes. Definitivamente ha cambiado la forma en que cuido la salud de Rocky.",
      verifiedBadge: true,
      location: "Monterrey",
      petImage: "/perrito13.jpeg",
      benefits: ["Diagnóstico rápido", "Atención personalizada", "Ahorro en visitas veterinarias"],
      followUp: "Detectamos una alergia alimentaria que otros veterinarios no habían identificado. Rocky está mucho mejor ahora."
    }
  ];
  
  // Actualizado: Reemplazando los premios no verificados con características confiables
  const trustFeatures = [
    { name: "Tecnología Respaldada por Veterinarios", icon: BadgeCheck },
    { name: "Innovación en Salud Animal", icon: Sparkles },
    { name: "Análisis Basado en Datos Reales", icon: Zap }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };
  
  const handlePrevTestimonial = () => {
    setAutoplay(false);
    setActiveTestimonial(prev => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };
  
  const handleNextTestimonial = () => {
    setAutoplay(false);
    setActiveTestimonial(prev => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  return (
    <section className="relative overflow-hidden py-12 sm:py-16 lg:py-24" id="testimonials">
      {/* Enhanced Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50/90 via-orange-50/90 to-white" />
        <div className="absolute inset-0 opacity-5">
          <PawPrint className="absolute h-16 sm:h-24 lg:h-48 w-16 sm:w-24 lg:w-48 -right-8 top-8 text-amber-900 rotate-12" />
          <PawPrint className="absolute h-16 sm:h-20 lg:h-40 w-16 sm:w-20 lg:w-40 -left-6 bottom-6 text-amber-900 -rotate-12" />
        </div>
        
        {/* Animated Background Elements */}
        <motion.div 
          className="absolute top-1/4 right-[10%] w-64 h-64 bg-amber-200/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        <motion.div 
          className="absolute bottom-1/4 left-[10%] w-64 h-64 bg-orange-200/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative">
        {/* Enhanced Header Content */}
        <div className="text-center mb-10 sm:mb-14 lg:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-1.5 sm:gap-2 bg-white/80 backdrop-blur-sm 
                     px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-sm mb-4 sm:mb-6"
          >
            <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
            <span className="text-amber-900 text-sm sm:text-base font-medium">Experiencias Reales</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4"
          >
            <span className="block text-amber-900">Lo que dicen las familias que</span>
            <span className="bg-gradient-to-r from-amber-600 to-orange-600 text-transparent bg-clip-text">
              confían en DogScan AI
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-base sm:text-lg lg:text-xl text-amber-700 max-w-3xl mx-auto mb-6 sm:mb-8"
          >
            Miles de dueños ya cuidan mejor a sus mascotas con nuestra tecnología de análisis avanzado
            y atención veterinaria personalizada
          </motion.p>
          
          {/* Trust Features - Actualizado para mostrar características confiables en lugar de premios */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap justify-center gap-3 sm:gap-5 mb-8 sm:mb-12"
          >
            {trustFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-sm 
                         border border-amber-100 flex items-center gap-2"
              >
                <feature.icon className="h-5 w-5 text-amber-600" />
                <span className="text-sm text-amber-800">{feature.name}</span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Enhanced Statistics */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 max-w-5xl mx-auto mb-10 sm:mb-14 lg:mb-16"
        >
          {statistics.map((stat, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-5 shadow-md border border-amber-100/50
                       hover:shadow-lg transition-all duration-300"
            >
              <div className="flex flex-col items-center">
                <div className="bg-amber-100/70 p-2 sm:p-3 rounded-full mb-3 sm:mb-4">
                  <stat.icon className="h-6 w-6 sm:h-8 sm:w-8 text-amber-600" />
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-amber-900 mb-1 sm:mb-2">{stat.value}</div>
                <div className="text-sm sm:text-base font-medium text-amber-700 mb-1">{stat.label}</div>
                <div className="text-xs text-amber-500 text-center">{stat.description}</div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Enhanced Testimonials Carousel */}
        <div className="mb-10 sm:mb-14 lg:mb-16 relative">
          <div className="flex justify-between items-center mb-6 sm:mb-8">
            <motion.h3
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-xl sm:text-2xl lg:text-3xl font-bold text-amber-900"
            >
              Experiencias de usuarios verificados
            </motion.h3>
            
            <div className="flex gap-2">
              <motion.button
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white w-10 h-10 rounded-full flex items-center justify-center 
                         shadow-sm border border-amber-100 hover:bg-amber-50 transition-colors"
                onClick={handlePrevTestimonial}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ChevronLeft className="h-5 w-5 text-amber-600" />
              </motion.button>
              <motion.button
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white w-10 h-10 rounded-full flex items-center justify-center 
                         shadow-sm border border-amber-100 hover:bg-amber-50 transition-colors"
                onClick={handleNextTestimonial}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ChevronRight className="h-5 w-5 text-amber-600" />
              </motion.button>
            </div>
          </div>
          
          <div className="relative">
            <AnimatePresence mode="sync">
              {testimonials.map((testimonial, index) => (
                activeTestimonial === index && (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-lg 
                             border border-amber-100/70"
                  >
                    <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-8">
                      {/* Left Column - User Info and Pet Image */}
                      <div className="lg:col-span-2 p-5 sm:p-6 md:p-8 bg-gradient-to-br from-amber-50 to-orange-50">
                        {/* User Info */}
                        <div className="flex items-start gap-3 sm:gap-4 mb-5 sm:mb-6">
                          <img
                            src={testimonial.image}
                            alt={testimonial.name}
                            className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover ring-2 ring-amber-100"
                          />
                          <div>
                            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap mb-1">
                              <h4 className="font-semibold text-amber-900 text-lg sm:text-xl">{testimonial.name}</h4>
                              {testimonial.verifiedBadge && (
                                <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <BadgeCheck className="h-3 w-3" />
                                  Verificado
                                </span>
                              )}
                            </div>
                            <p className="text-amber-600 text-sm sm:text-base">{testimonial.role}</p>
                            <p className="text-amber-500 text-sm">{testimonial.breed}</p>
                            
                            {/* Rating */}
                            <div className="flex items-center gap-1 mt-2">
                              {[...Array(testimonial.rating)].map((_, i) => (
                                <Star key={i} className="h-4 w-4 sm:h-5 sm:w-5 text-amber-400 fill-current" />
                              ))}
                              <span className="text-amber-700 text-sm ml-1">5.0</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Pet Image */}
                        <div className="mb-5 sm:mb-6">
                          <div className="relative rounded-lg overflow-hidden border-2 border-amber-100">
                            <img 
                              src={testimonial.petImage} 
                              alt={`Mascota de ${testimonial.name}`}
                              className="w-full h-48 sm:h-56 object-cover"
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-amber-900/70 to-transparent p-3">
                              <p className="text-white text-sm font-medium">
                                Mascota analizada con DogScan AI
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Location and Date */}
                        <div className="flex items-center justify-between text-sm text-amber-500 mb-4">
                          <span className="flex items-center gap-1">
                            <MessageCircle className="h-4 w-4" />
                            {testimonial.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {testimonial.date}
                          </span>
                        </div>
                        
                        {/* Benefits */}
                        <div className="bg-white rounded-lg p-4 shadow-sm border border-amber-100">
                          <h5 className="font-medium text-amber-900 mb-2 text-sm">Beneficios obtenidos:</h5>
                          <ul className="space-y-2">
                            {testimonial.benefits.map((benefit, i) => (
                              <li key={i} className="flex items-start gap-2 text-amber-700 text-sm">
                                <CheckCircle2 className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                <span>{benefit}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      
                      {/* Right Column - Testimonial Content */}
                      <div className="lg:col-span-3 p-5 sm:p-6 md:p-8 flex flex-col">
                        <div className="mb-4 sm:mb-6">
                          <Quote className="h-10 w-10 sm:h-12 sm:w-12 text-amber-200 mb-4" />
                          
                          <div className="bg-amber-50/50 rounded-lg p-4 sm:p-5 border border-amber-100 mb-4 sm:mb-6 relative">
                            <div className="absolute top-0 right-0 transform translate-x-1/3 -translate-y-1/3">
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                                className="text-amber-400/20"
                              >
                                <PawPrint className="h-16 w-16 sm:h-20 sm:w-20" />
                              </motion.div>
                            </div>
                            
                            <p className="text-amber-800 text-base sm:text-lg leading-relaxed relative z-10">
                              "{testimonial.content}"
                            </p>
                          </div>
                          
                          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4 sm:p-5 border border-amber-100">
                            <h5 className="font-medium text-amber-900 mb-2 flex items-center gap-2">
                              <ThumbsUp className="h-5 w-5 text-amber-500" />
                              Resultados obtenidos:
                            </h5>
                            <p className="text-amber-700 text-sm sm:text-base">
                              {testimonial.followUp}
                            </p>
                          </div>
                        </div>
                        
                        <div className="mt-auto">
                          <div className="bg-amber-100/50 rounded-lg p-4 sm:p-5 mb-4 sm:mb-6">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-amber-900">Análisis realizado</h5>
                              <span className="bg-amber-200 text-amber-800 text-xs px-2 py-0.5 rounded-full">
                                Premium
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <span className="bg-white text-amber-700 px-3 py-1 rounded-full text-sm border border-amber-100">
                                Análisis Nutricional
                              </span>
                              <span className="bg-white text-amber-700 px-3 py-1 rounded-full text-sm border border-amber-100">
                                Detección Preventiva
                              </span>
                              <span className="bg-white text-amber-700 px-3 py-1 rounded-full text-sm border border-amber-100">
                                Consulta Especializada
                              </span>
                            </div>
                          </div>
                          
                          <Button
                            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 
                                     text-white py-3 text-base sm:text-lg rounded-xl shadow-md hover:shadow-lg"
                            onClick={() => window.location.href = '/?sign-in=true'}
                          >
                            Obtén resultados similares para tu mascota
                            <ChevronRight className="ml-2 h-5 w-5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )
              ))}
            </AnimatePresence>
            
            {/* Pagination Dots */}
            <div className="flex justify-center gap-2 mt-6">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 
                           ${activeTestimonial === index ? 'bg-amber-600 w-6' : 'bg-amber-200'}`}
                  onClick={() => {
                    setAutoplay(false);
                    setActiveTestimonial(index);
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;